/* eslint-disable no-bitwise */
import { getLayoutSettings, LayoutSettings } from './local-printer.settings';

/**
 * ESC/POS command builder.
 *
 * Builds a byte buffer the same way the connector's SerialPrinter does, so the
 * output can be compared against it directly. Text is encoded as CP865
 * (Nordic) with printer character table 2, matching SerialPrinterSettings.
 *
 * Ported from POSUI client/js/shared/serial/escpos.service.js.
 *
 *   const b = escposBuilder();
 *   b.init().center().large().line('SHOP').normal().left().feed(1).cut();
 *   await webSerial.write(b.bytes());
 */

const ESC = 0x1b;
const GS = 0x1d;
const DLE = 0x10;
const EOT = 0x04;

/**
 * Unicode -> CP865. Only the characters a Nordic/Western receipt uses;
 * anything unmapped becomes '?' rather than a byte the printer would render
 * as line-art.
 */
const CP865: { [ch: string]: number } = {
  Ç: 0x80,
  ü: 0x81,
  é: 0x82,
  â: 0x83,
  ä: 0x84,
  à: 0x85,
  å: 0x86,
  ç: 0x87,
  ê: 0x88,
  ë: 0x89,
  è: 0x8a,
  ï: 0x8b,
  î: 0x8c,
  ì: 0x8d,
  Ä: 0x8e,
  Å: 0x8f,
  É: 0x90,
  æ: 0x91,
  Æ: 0x92,
  ô: 0x93,
  ö: 0x94,
  ò: 0x95,
  û: 0x96,
  ù: 0x97,
  ÿ: 0x98,
  Ö: 0x99,
  Ü: 0x9a,
  ø: 0x9b,
  '£': 0x9c,
  Ø: 0x9d,
  '₧': 0x9e,
  ƒ: 0x9f,
  á: 0xa0,
  í: 0xa1,
  ó: 0xa2,
  ú: 0xa3,
  ñ: 0xa4,
  Ñ: 0xa5,
  ª: 0xa6,
  º: 0xa7,
  '¿': 0xa8,
  '¬': 0xaa,
  '½': 0xab,
  '¼': 0xac,
  '¡': 0xad,
  '«': 0xae,
  '¤': 0xaf,
  ß: 0xe1,
  µ: 0xe6,
  '±': 0xf1,
  '÷': 0xf6,
  '°': 0xf8,
  '·': 0xfa,
  '²': 0xfd
};

export const encode = (text: unknown): number[] =>
  Array.from(String(text === undefined || text === null ? '' : text)).map(
    ch => {
      const code = ch.charCodeAt(0);
      if (code === 10) return 0x0a;
      if (code < 0x80) return code;
      return CP865[ch] !== undefined ? CP865[ch] : 0x3f;
    }
  );

export type EscPosBuilder = {
  init: () => EscPosBuilder;
  codePage: (n: number) => EscPosBuilder;
  left: () => EscPosBuilder;
  center: () => EscPosBuilder;
  right: () => EscPosBuilder;
  size: (width: number, height: number) => EscPosBuilder;
  normal: () => EscPosBuilder;
  large: () => EscPosBuilder;
  wide: () => EscPosBuilder;
  bold: (on?: boolean) => EscPosBuilder;
  text: (value: unknown) => EscPosBuilder;
  line: (value?: unknown) => EscPosBuilder;
  feed: (lines?: number) => EscPosBuilder;
  rule: (char?: string) => EscPosBuilder;
  barcode39: (value: string, height?: number, width?: number) => EscPosBuilder;
  cut: () => EscPosBuilder;
  openDrawer: () => EscPosBuilder;
  raw: (bytes: number[]) => EscPosBuilder;
  bytes: () => Uint8Array;
  columns: number;
};

export const escposBuilder = (
  layout: LayoutSettings = getLayoutSettings()
): EscPosBuilder => {
  const buf: number[] = [];

  const b: EscPosBuilder = {
    columns: layout.columns,

    raw: bytes => {
      buf.push(...bytes);
      return b;
    },

    /** ESC @ - reset, then select the configured character table. */
    init: () => b.raw([ESC, 0x40]).codePage(layout.printerCodePage),

    /** ESC t n */
    codePage: n => b.raw([ESC, 0x74, n]),

    left: () => b.raw([ESC, 0x61, 0]),
    center: () => b.raw([ESC, 0x61, 1]),
    right: () => b.raw([ESC, 0x61, 2]),

    /** GS ! n - width/height multipliers packed into one byte. */
    size: (width, height) => {
      const w = Math.max(0, Math.min(7, (width || 1) - 1));
      const h = Math.max(0, Math.min(7, (height || 1) - 1));
      return b.raw([GS, 0x21, (w << 4) | h]);
    },
    normal: () => b.size(1, 1),
    large: () => b.size(2, 2),
    wide: () => b.size(2, 1),

    /** ESC E n */
    bold: (on = true) => b.raw([ESC, 0x45, on ? 1 : 0]),

    text: value => b.raw(encode(value)),
    line: (value = '') => b.text(value).text('\n'),

    /** ESC d n */
    feed: (lines = 1) =>
      lines <= 0 ? b : b.raw([ESC, 0x64, Math.min(255, lines)]),

    /** A full-width row of one character. */
    rule: (char = '-') => b.line(char.repeat(layout.columns)),

    /**
     * CODE39 via GS k, as the connector prints the receipt number.
     * Height and width follow the ODP333 values in the connector README.
     */
    barcode39: (value, height = 162, width = 1) => {
      const data = String(value || '').toUpperCase();
      if (!data) return b;
      return b
        .raw([GS, 0x68, height]) // GS h - height
        .raw([GS, 0x77, width]) // GS w - module width
        .raw([GS, 0x48, 2]) // GS H - human-readable text below
        .raw([GS, 0x6b, 69, data.length]) // GS k 69 n - CODE39, explicit length
        .raw(encode(data));
    },

    /** GS V 66 n - feed then partial cut. */
    cut: () => b.raw([GS, 0x56, 66, 0x00]),

    /** ESC p m t1 t2 - kick the cash drawer on pin 2. */
    openDrawer: () => b.raw([ESC, 0x70, 0x00, 0x19, 0xfa]),

    bytes: () => new Uint8Array(buf)
  };

  return b;
};

/** Real-time status queries (DLE EOT n). Each answers with a single byte. */
export const statusRequest = {
  printer: new Uint8Array([DLE, EOT, 1]),
  offline: new Uint8Array([DLE, EOT, 2]),
  error: new Uint8Array([DLE, EOT, 3]),
  paper: new Uint8Array([DLE, EOT, 4])
};

export type PaperStatus = 'Good' | 'NearEnd' | 'End' | 'Unknown';

/**
 * Decode the paper roll status byte (DLE EOT 4). Same bit masks as
 * SerialPrinter.PaperStatus() in the connector, so both agree on a byte.
 */
export const decodePaperStatus = (byte: number | null): PaperStatus => {
  if (byte === null || byte === undefined) return 'Unknown';
  if ((byte & 96) !== 0) return 'End'; // bits 5,6 - roll end
  if ((byte & 0x0c) !== 0) return 'NearEnd'; // bits 2,3 - near end
  return 'Good';
};
